import {
	faCalendar,
	faLink,
	faPeopleGroup,
	faSuitcase,
	faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";

// Extract constants for styles and error messages
const ERROR_STYLE = { color: "red", margin: 0, fontSize: "14px" };
const SUBMIT_BTN_CLASS = "scclass-submitbtn";
const DEFAULT_COURSE_OPTION = { coursetitle: "Select the Course" };
const DEFAULT_INSTRUCTOR_OPTION = { name: "Select the Instructor" };

const Schedule = () => {
	const { auth } = useSelector((state) => state);
	const dispatch = useDispatch();
	const [allClasses, setAllClasses] = useState([]);
	const [formData, setFormData] = useState({
		name: "",
		organisationname: "",
		classtype: "",
		grouptype: "",
		dateandtime: null,
		noofmembers: "",
		course: "",
		instructor: "",
		proposal: "",
		listofmembers: "",
		URL: "",
	});
	const [errors, setErrors] = useState({});
	const [selectedDate, setSelectedDate] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenInstructor, setIsOpenInstructor] = useState(false);
	const [selectedOption, setSelectedOption] = useState(DEFAULT_COURSE_OPTION);
	const [fileUploaded, setFileUploaded] = useState("");
	const [instructorsList, setInstructorsList] = useState([]);
	const [selectedInstructorOption, setSelectedInstructorOption] = useState(
		DEFAULT_INSTRUCTOR_OPTION
	);
	const [coursesArray, setCoursesArray] = useState([]);
	const dropdownRef = useRef(null);
	const dropdownRef1 = useRef(null);

	// Fetch all classes
	useEffect(() => {
		const fetchAllClasses = async () => {
			if (!auth.token) return;
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
			try {
				const res = await getDataAPI("/getAllClasses", auth.token);
				setAllClasses(res.data.schedule_class || []);
			} catch (err) {
				console.error("Error fetching classes:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch classes" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};
		fetchAllClasses();
	}, [auth.token, dispatch]);

	// Fetch courses
	useEffect(() => {
		const fetchCourses = async () => {
			if (!auth.token) return;
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
			try {
				const res = await getDataAPI(
					`getAllCoursesNames/${auth.user.id}/9ae8b71c-dd3f-4fad-b1ee-e872f951e7af`,
					auth.token
				);
				setCoursesArray(res.data.courses || []);
			} catch (err) {
				console.error("Error fetching courses:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch courses" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};
		fetchCourses();
	}, [auth.token, auth.user?.id, dispatch]);

	// Fetch instructors based on selected course
	useEffect(() => {
		const fetchInstructors = async () => {
			if (selectedOption && selectedOption.id && auth.token) {
				try {
					const res = await getDataAPI(
						`getInstructorsNameForCourse/${selectedOption.id}`,
						auth.token
					);
					setInstructorsList(res.data.instructors || []);
				} catch (err) {
					console.error("Error fetching instructors:", err);
				}
			}
		};
		fetchInstructors();
	}, [selectedOption, auth.token]);

	// Handle form data input changes
	const handleInputChange = useCallback((e) => {
		const { name, value } = e.target;
		setFormData((prevState) => ({
			...prevState,
			[name]: value,
		}));
	}, []);

	// Handle course selection
	const handleOptionClick = useCallback((option) => {
		setSelectedOption(option);
		setIsOpen(false);
		setFormData((prevState) => ({
			...prevState,
			course: option,
		}));
	}, []);

	// Handle instructor selection
	const handleOptionClickInstructor = useCallback((option) => {
		setSelectedInstructorOption(option);
		setIsOpenInstructor(false);
		setFormData((prevState) => ({
			...prevState,
			instructor: option,
		}));
	}, []);

	// Handle dropdown toggle
	const toggleDropdown = useCallback(() => setIsOpen((prev) => !prev), []);
	const toggleDropdownInstructor = useCallback(
		() => setIsOpenInstructor((prev) => !prev),
		[]
	);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				dropdownRef1.current &&
				!dropdownRef1.current.contains(event.target)
			) {
				setIsOpen(false);
				setIsOpenInstructor(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	// Handle members file upload
	const handleMembersUpload = useCallback((e) => {
		e.preventDefault();
		const files = e.target.files;
		const f = files[0];
		if (
			f &&
			f.type !==
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		) {
			alert("Please upload an Excel file.");
			return;
		}
		const reader = new FileReader();
		reader.onload = function (e) {
			const data = e.target.result;
			let readedData = XLSX.read(data, { type: "binary" });
			const wsname = readedData.SheetNames[1];
			const ws = readedData.Sheets[wsname];
			const dataParse = XLSX.utils.sheet_to_json(ws, { header: 2 });
			if (dataParse.length === 0)
				alert("Please upload the file in specified format");
			setFileUploaded(dataParse);
			setFormData((prev) => ({ ...prev, listofmembers: dataParse }));
		};
		reader.readAsBinaryString(f);
	}, []);

	// Handle proposal file upload
	const handleProposalUpload = useCallback((e) => {
		e.preventDefault();
		const file = e.target.files[0];
		if (file) {
			if (file.type !== "application/pdf") {
				alert("Please upload a PDF file.");
				return;
			}
			setFormData((prevState) => ({
				...prevState,
				proposal: file,
			}));
		}
	}, []);

	// Validation function
	const validateForm = useCallback(() => {
		let formErrors = {};
		if (!formData.name) formErrors.name = "Class name is required";
		if (!formData.organisationname)
			formErrors.organisationname = "Organisation name is required";
		if (!formData.classtype) formErrors.classtype = "Class type is required";
		if (!formData.dateandtime)
			formErrors.dateandtime = "Date and time are required";
		if (!formData.noofmembers)
			formErrors.noofmembers = "Number of members is required";
		if (!formData.course || formData.course === "Select the Course")
			formErrors.course = "Course selection is required";
		if (!formData.instructor || formData.instructor === "Select the Instructor")
			formErrors.instructor = "Instructor selection is required";
		if (!formData.URL) formErrors.URL = "URL is required";
		if (formData.classtype === "Virtual" && !formData.grouptype)
			formErrors.grouptype = "Group type is required for virtual classes";
		if (!formData.listofmembers)
			formErrors.listofmembers = "List of members file is required.";
		if (!formData.proposal.name)
			formErrors.proposal = "Proposal file is required.";
		else if (formData.proposal.name.split(".").length > 2)
			formErrors.invalidProposal = "Select valid pdf file";
		else if (formData.proposal.type !== "application/pdf")
			formErrors.invalidProposal = "Proposal file should be in pdf file";
		setErrors(formErrors);
		return Object.keys(formErrors).length === 0;
	}, [formData]);

	// Handle form submission
	const handleSubmit = useCallback(
		async (e) => {
			e.preventDefault();
			if (!validateForm()) return;
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });
			const sendData = new FormData();
			if (formData.proposal !== "") {
				sendData.append("files", formData.proposal);
			}
			for (let key in formData) {
				sendData.append(
					key,
					typeof formData[key] === "object"
						? JSON.stringify(formData[key])
						: formData[key]
				);
			}
			try {
				const res = await postDataAPI(
					"createscheduleclass",
					sendData,
					auth.token
				);
				if (res.status === 200) {
					alert("Class Created Successfully!!!");
					window.location.reload();
				}
			} catch (err) {
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error: err.response?.data?.msg || "Failed to create class",
					},
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[formData, dispatch, auth.token, validateForm]
	);

	// Memoized course and instructor options
	const courseOptions = useMemo(() => coursesArray || [], [coursesArray]);
	const instructorOptions = useMemo(
		() => instructorsList || [],
		[instructorsList]
	);

	return (
		<>
			<Adminnavbar />
			<div className="scclass">
				<div className="scclass-box">
					<div className="scclass-heading">
						<h2>Schedule a Class</h2>
					</div>
					<div className="scclass-boxIn">
						<div className="scclass-boxIn-content">
							<div className="scclass-greenbox">
								<h4 className="scclass-greenbox-heading">Fill the form</h4>
								<form onSubmit={handleSubmit} autoComplete="off">
									{/* Class Name */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<input
												type="text"
												placeholder="Class Name"
												name="name"
												value={formData.name}
												onChange={handleInputChange}
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faUser} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.name ? "visible" : "hidden",
										}}
										className="error">
										{errors.name || "Nothing"}
									</p>
									{/* Organization Name */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<input
												type="text"
												placeholder="Organisation Name"
												name="organisationname"
												value={formData.organisationname}
												onChange={handleInputChange}
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faUser} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.organisationname
												? "visible"
												: "hidden",
										}}
										className="error">
										{errors.organisationname || "Nothing"}
									</p>
									{/* Class Type */}
									<div className="js-inputbox">
										<span className="js-inputbox-input">
											<span style={{ fontSize: "16px" }}>Type: </span>
											<label style={{ marginLeft: "5px" }}>
												<input
													type="radio"
													name="classtype"
													value="Virtual"
													checked={formData.classtype === "Virtual"}
													onChange={handleInputChange}
												/>
												Virtual
											</label>
											<label style={{ marginLeft: "5px" }}>
												<input
													type="radio"
													name="classtype"
													value="Onsite"
													checked={formData.classtype === "Onsite"}
													onChange={handleInputChange}
												/>
												Onsite
											</label>
										</span>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.classtype ? "visible" : "hidden",
										}}
										className="error">
										{errors.classtype || "Nothing"}
									</p>
									{/* Group Type for Virtual Classes */}
									{formData.classtype === "Virtual" && (
										<>
											<div className="scclass-inputbox">
												<span className="scclass-inputbox-input">
													<div>
														<span style={{ fontSize: "16px" }}>Link: </span>
														<label style={{ marginLeft: "5px" }}>
															<input
																type="radio"
																name="grouptype"
																value="Single"
																checked={formData.grouptype === "Single"}
																onChange={handleInputChange}
															/>
															Single
														</label>
														<label style={{ marginLeft: "5px" }}>
															<input
																type="radio"
																name="grouptype"
																value="Multi"
																checked={formData.grouptype === "Multi"}
																onChange={handleInputChange}
															/>
															Multi
														</label>
													</div>
												</span>
											</div>
											<p
												style={{
													...ERROR_STYLE,
													visibility: errors.grouptype ? "visible" : "hidden",
												}}
												className="error">
												{errors.grouptype || "Nothing"}
											</p>
										</>
									)}
									{/* Date and Time */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<DatePicker
												style={{ width: "100%", height: "100%" }}
												showTimeSelect
												dateFormat="Pp"
												selected={formData.dateandtime}
												onChange={(date) =>
													setFormData((prev) => ({
														...prev,
														dateandtime: date,
													}))
												}
												placeholderText="Date and Time"
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faCalendar} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.dateandtime ? "visible" : "hidden",
										}}
										className="error">
										{errors.dateandtime || "Nothing"}
									</p>
									{/* No. of Members */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<input
												type="text"
												placeholder="No. of members"
												name="noofmembers"
												value={formData.noofmembers}
												onChange={handleInputChange}
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faPeopleGroup} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.noofmembers ? "visible" : "hidden",
										}}
										className="error">
										{errors.noofmembers || "Nothing"}
									</p>
									{/* Course Selection */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<div
												className="scclass-dropdown-container"
												ref={dropdownRef}>
												<div className="scclass-dropdown">
													<div
														className="scclass-dropdown-header"
														onClick={toggleDropdown}>
														{selectedOption.coursetitle}
														<span
															className={`scclass-dropdown-symbol ${
																isOpen ? "open" : ""
															}`}
														/>
													</div>
													{isOpen && (
														<ul className="scclass-dropdown-list">
															{courseOptions.map((option, index) => (
																<li
																	key={index}
																	className="scclass-dropdown-item"
																	onClick={() => handleOptionClick(option)}>
																	{option.coursetitle}
																</li>
															))}
														</ul>
													)}
												</div>
											</div>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faSuitcase} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.course ? "visible" : "hidden",
										}}
										className="error">
										{errors.course || "Nothing"}
									</p>
									{/* Instructor Selection */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<div
												className="scclass-dropdown-container"
												ref={dropdownRef1}>
												<div className="scclass-dropdown">
													<div
														className="scclass-dropdown-header"
														onClick={toggleDropdownInstructor}>
														{selectedInstructorOption.name}
														<span
															className={`scclass-dropdown-symbol ${
																isOpenInstructor ? "open" : ""
															}`}
														/>
													</div>
													{isOpenInstructor && (
														<ul className="scclass-dropdown-list">
															{instructorOptions.map((option, index) => (
																<li
																	key={index}
																	className="scclass-dropdown-item"
																	onClick={() =>
																		handleOptionClickInstructor(option)
																	}>
																	{option.name}
																</li>
															))}
														</ul>
													)}
												</div>
											</div>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faSuitcase} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.instructor ? "visible" : "hidden",
										}}
										className="error">
										{errors.instructor || "Nothing"}
									</p>
									{/* Proposal File Upload */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<label>Proposal: </label>
											<input
												type="file"
												onChange={handleProposalUpload}
												placeholder="Proposal"
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faPeopleGroup} />
										</div>
									</div>
									<p
										className="error-message"
										style={{
											...ERROR_STYLE,
											visibility:
												errors.proposal || errors.invalidProposal
													? "visible"
													: "hidden",
										}}>
										{errors.proposal
											? errors.proposal
											: errors.invalidProposal || "Nothing"}
									</p>
									{/* List of Members File Upload */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<label>List of Members: </label>
											<input
												type="file"
												onChange={handleMembersUpload}
												accept=".xlsx,.xls"
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faPeopleGroup} />
										</div>
									</div>
									<p
										className="error-message"
										style={{
											...ERROR_STYLE,
											visibility:
												errors.listofmembers || errors.invalidListofmembers
													? "visible"
													: "hidden",
										}}>
										{errors.listofmembers
											? errors.listofmembers
											: errors.invalidListofmembers || "Nothing"}
									</p>
									{/* URL */}
									<div className="scclass-inputbox">
										<span className="scclass-inputbox-input">
											<input
												type="text"
												placeholder="Class room URL"
												name="URL"
												value={formData.URL}
												onChange={handleInputChange}
											/>
										</span>
										<div className="scclass-Icon">
											<FontAwesomeIcon icon={faLink} />
										</div>
									</div>
									<p
										style={{
											...ERROR_STYLE,
											visibility: errors.URL ? "visible" : "hidden",
										}}
										className="error">
										{errors.URL || "Nothing"}
									</p>
									{/* Submit Button */}
									<button type="submit" className={SUBMIT_BTN_CLASS}>
										SUBMIT
									</button>
								</form>
							</div>
						</div>
						<div className="scclass-boxIn-Image">
							<div className="scclass-boxIn-ImageIn">
								<img src="../images/Instructor-Led.png" alt="instructor" />
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default Schedule;
